import { type RefObject, useEffect, useRef } from 'react';
import { PROGRAMMATIC_FOCUS } from '@/components/library-page/library-page-layer';

/** How long a plugin's page or the list may take to show (lazy data), in frames. */
const FOCUS_WAIT_FRAMES = 90;

/**
 * Focus follows a plugin's page (M5): its title when it opens, the card or row that opened it
 * (`data-plugin-open`) when it closes — unless the focus already went somewhere else.
 */
export function usePluginPageFocus(
  pluginName: string | null,
  root: RefObject<HTMLElement | null>,
): void {
  const previous = useRef(pluginName);
  useEffect(() => {
    const closed = previous.current;
    previous.current = pluginName;
    if (closed === pluginName) return;
    const target = (): HTMLElement | null => {
      const container = root.current;
      if (container === null) return null;
      if (pluginName !== null) return container.querySelector<HTMLElement>('h1');
      return (
        [...container.querySelectorAll<HTMLElement>('[data-plugin-open]')].find(
          (button) => button.dataset['pluginOpen'] === closed,
        ) ?? null
      );
    };
    let frames = 0;
    let frame = requestAnimationFrame(function move() {
      const active = document.activeElement;
      // Lost with what was removed, or on a page title the app focused itself.
      const free = active === null || active === document.body || active.tagName === 'H1';
      const element = free ? target() : null;
      if (element !== null) {
        if (pluginName !== null) {
          if (!element.hasAttribute('tabindex')) element.tabIndex = -1;
          element.setAttribute(PROGRAMMATIC_FOCUS, '');
        }
        element.focus({ preventScroll: pluginName !== null });
        return;
      }
      frames += 1;
      if (free && frames < FOCUS_WAIT_FRAMES) frame = requestAnimationFrame(move);
    });
    return () => cancelAnimationFrame(frame);
  }, [pluginName, root]);
}
